import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle, RefreshCw, Download, Search, X, ChevronDown,
  ChevronRight, Activity, Cpu, Globe, Shield, Monitor, Zap,
  CheckCircle, Clock, Filter, Calendar, Terminal
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────
const LEVELS = ['ALL', 'INFO', 'WARNING', 'ERROR', 'CRITICAL', 'DEBUG'];
const CATEGORIES = ['ALL', 'REQUEST', 'ERROR', 'LLM', 'AUTH', 'SYSTEM', 'FRONTEND', 'ENRICHMENT', 'TTS'];

const LEVEL_STYLE = {
  INFO:     { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/30'  },
  WARNING:  { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/30' },
  ERROR:    { bg: 'bg-red-500/20',    text: 'text-red-300',    border: 'border-red-500/40'    },
  CRITICAL: { bg: 'bg-red-600/30',    text: 'text-red-200',    border: 'border-red-400/60'    },
  DEBUG:    { bg: 'bg-gray-500/10',   text: 'text-gray-400',   border: 'border-gray-500/20'   },
};

const CAT_ICON = {
  REQUEST:    <Globe size={13} />,
  ERROR:      <AlertTriangle size={13} />,
  LLM:        <Zap size={13} />,
  AUTH:       <Shield size={13} />,
  SYSTEM:     <Cpu size={13} />,
  FRONTEND:   <Monitor size={13} />,
  ENRICHMENT: <Activity size={13} />,
  TTS:        <Activity size={13} />,
};

function fmt(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ts; }
}

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return d; }
}

// ── LogEntry row ─────────────────────────────────────────────────────────────
function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const style = LEVEL_STYLE[entry.level] || LEVEL_STYLE.DEBUG;
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <div
      className={`border-b border-gray-800 ${entry.is_critical ? 'bg-red-950/20' : ''}`}
      data-testid="log-entry"
    >
      <div
        className={`flex items-start gap-2 px-3 py-2 text-xs font-mono hover:bg-gray-800/40 transition-colors ${hasDetails ? 'cursor-pointer' : ''}`}
        onClick={() => hasDetails && setOpen(o => !o)}
      >
        {/* Time */}
        <span className="text-gray-500 shrink-0 w-20">{fmt(entry.timestamp)}</span>

        {/* Level badge */}
        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${style.bg} ${style.text} ${style.border}`}>
          {entry.level}
        </span>

        {/* Category */}
        <span className="shrink-0 flex items-center gap-1 text-gray-500 w-24">
          {CAT_ICON[entry.category] || null}
          <span className="truncate">{entry.category}</span>
        </span>

        {/* Message */}
        <span className={`flex-1 break-all leading-relaxed ${entry.is_critical ? 'text-red-200' : 'text-gray-200'}`}>
          {entry.message}
        </span>

        {/* Duration */}
        {entry.duration_ms != null && (
          <span className={`shrink-0 text-right w-16 ${entry.duration_ms > 2000 ? 'text-yellow-400' : 'text-gray-500'}`}>
            {entry.duration_ms < 1000 ? `${Math.round(entry.duration_ms)}ms` : `${(entry.duration_ms / 1000).toFixed(1)}s`}
          </span>
        )}

        {/* Expand arrow */}
        {hasDetails && (
          <span className="shrink-0 text-gray-600">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </span>
        )}
      </div>

      {/* Expanded details */}
      {open && hasDetails && (
        <div className="px-4 pb-3 pt-1 bg-gray-900/60 border-t border-gray-800/60">
          <pre className="text-[11px] text-gray-400 whitespace-pre-wrap font-mono overflow-x-auto max-h-64">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Critical alert card ───────────────────────────────────────────────────────
function AlertCard({ alert, onDismiss }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-red-500/40 bg-red-950/30 p-3 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <AlertTriangle size={14} className="text-red-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-red-200 font-medium break-all">{alert.message}</div>
            <div className="text-gray-500 mt-0.5">
              {fmtDate(alert.date)} · {fmt(alert.timestamp)} · {alert.category}
              {alert.request_path && ` · ${alert.request_path}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {alert.details && Object.keys(alert.details).length > 0 && (
            <button onClick={() => setOpen(o => !o)} className="text-gray-500 hover:text-gray-300 p-1">
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          )}
          <button
            onClick={() => onDismiss(alert.alert_id)}
            className="text-gray-500 hover:text-red-400 p-1 transition-colors"
            title="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      </div>
      {open && alert.details && (
        <pre className="mt-2 text-[11px] text-gray-400 whitespace-pre-wrap font-mono overflow-x-auto max-h-48 border-t border-red-900/40 pt-2">
          {JSON.stringify(alert.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AdminLogs() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [level, setLevel] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 150;

  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alertsOpen, setAlertsOpen] = useState(true);

  const [summary, setSummary] = useState([]);
  const [availDates, setAvailDates] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [stats, setStats] = useState(null);

  const refreshRef = useRef(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard');
  }, [user, navigate]);

  const fetchEntries = useCallback(async (resetOffset = true) => {
    setLoading(true);
    const off = resetOffset ? 0 : offset;
    if (resetOffset) setOffset(0);
    try {
      const params = { date, limit: LIMIT, offset: off };
      if (level !== 'ALL') params.level = level;
      if (category !== 'ALL') params.category = category;
      if (search) params.search = search;
      const res = await api.get('/logs/entries', { params });
      if (resetOffset) {
        setEntries(res.data.entries || []);
      } else {
        setEntries(prev => [...prev, ...(res.data.entries || [])]);
      }
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [date, level, category, search, offset]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/logs/critical-alerts');
      setAlerts(res.data.alerts || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const [sumRes, dateRes] = await Promise.all([
        api.get('/logs/summary', { params: { days: 7 } }),
        api.get('/logs/dates'),
      ]);
      setSummary(sumRes.data || []);
      setAvailDates(dateRes.data || []);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/logs/stats/${date}`);
      setStats(res.data);
    } catch {}
  }, [date]);

  // Initial load
  useEffect(() => { fetchEntries(true); }, [date, level, category, search]);
  useEffect(() => { fetchAlerts(); fetchMeta(); }, []);
  useEffect(() => { fetchStats(); }, [date]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshRef.current = setInterval(() => { fetchEntries(true); fetchAlerts(); }, 15000);
    } else {
      clearInterval(refreshRef.current);
    }
    return () => clearInterval(refreshRef.current);
  }, [autoRefresh, fetchEntries, fetchAlerts]);

  const handleDismiss = async (alertId) => {
    try {
      await api.post(`/logs/critical-alerts/${alertId}/dismiss`);
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { toast.error('Failed to dismiss alert'); }
  };

  const handleDismissAll = async () => {
    try {
      await api.post('/logs/critical-alerts/dismiss-all');
      setAlerts([]);
      setUnreadCount(0);
      toast.success('All alerts dismissed');
    } catch { toast.error('Failed to dismiss alerts'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/logs/export/${date}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `iling-logs-${date}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported logs for ${date}`);
    } catch { toast.error('Export failed'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const loadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchEntries(false);
  };

  const dayTotal = summary.find(s => s.date === date);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" style={{ fontFamily: 'monospace' }}>
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/80 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Terminal size={18} className="text-green-400" />
            <h1 className="text-base font-bold text-green-400 tracking-wider">SYSTEM LOGS</h1>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse">
                {unreadCount} critical
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchEntries(true); fetchAlerts(); fetchMeta(); }}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
              data-testid="refresh-logs-btn"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={() => setAutoRefresh(p => !p)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${autoRefresh ? 'bg-green-900/40 text-green-400 border border-green-500/30' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}
              data-testid="auto-refresh-toggle"
            >
              <Activity size={13} />
              {autoRefresh ? 'Live' : 'Auto'}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
              data-testid="export-logs-btn"
            >
              <Download size={13} />
              Export
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">

        {/* ── 7-day Spark bar ─────────────────────────────────────────────── */}
        {summary.length > 0 && (
          <div className="flex items-end gap-1 h-10" title="Last 7 days — click to jump to a day">
            {[...summary].reverse().map(s => {
              const maxTotal = Math.max(...summary.map(x => x.total), 1);
              const h = Math.max(4, (s.total / maxTotal) * 36);
              const isSelected = s.date === date;
              const hasErrors = s.ERROR + s.CRITICAL > 0;
              return (
                <button
                  key={s.date}
                  onClick={() => setDate(s.date)}
                  title={`${fmtDate(s.date)}: ${s.total} events (${s.ERROR + s.CRITICAL} errors)`}
                  className="flex flex-col items-center gap-0.5 group"
                  style={{ minWidth: 28 }}
                >
                  <div
                    className={`rounded-sm w-full transition-all ${
                      hasErrors ? 'bg-red-500/60 group-hover:bg-red-400' :
                      isSelected ? 'bg-green-400' : 'bg-gray-700 group-hover:bg-gray-500'
                    } ${isSelected ? 'ring-1 ring-green-400' : ''}`}
                    style={{ height: h }}
                  />
                  <span className="text-[9px] text-gray-600">{s.date.slice(5)}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Critical alerts ──────────────────────────────────────────────── */}
        {alerts.length > 0 && (
          <div className="rounded-lg border border-red-500/30 bg-gray-900/80">
            <button
              className="w-full flex items-center justify-between px-3 py-2 text-xs"
              onClick={() => setAlertsOpen(o => !o)}
              data-testid="alerts-section-toggle"
            >
              <span className="flex items-center gap-2 text-red-300 font-bold">
                <AlertTriangle size={14} className="text-red-400" />
                CRITICAL ALERTS ({alerts.length})
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={e => { e.stopPropagation(); handleDismissAll(); }}
                  className="text-gray-500 hover:text-red-300 text-[10px] px-2 py-0.5 rounded hover:bg-red-900/30 transition-colors"
                  data-testid="dismiss-all-alerts-btn"
                >
                  Dismiss all
                </button>
                {alertsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </div>
            </button>
            {alertsOpen && (
              <div className="px-3 pb-3 space-y-2 border-t border-red-900/30">
                {alerts.slice(0, 20).map(a => (
                  <AlertCard key={a.alert_id} alert={a} onDismiss={handleDismiss} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Filters row ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Date picker */}
          <div className="flex items-center gap-1.5 bg-gray-900 border border-gray-700 rounded px-2 py-1">
            <Calendar size={13} className="text-gray-500" />
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              max={today}
              className="bg-transparent text-xs text-gray-200 outline-none cursor-pointer"
              data-testid="log-date-picker"
            />
          </div>

          {/* Level filter */}
          <div className="flex items-center gap-1 bg-gray-900 border border-gray-700 rounded px-1 py-0.5">
            {LEVELS.map(l => (
              <button
                key={l}
                onClick={() => setLevel(l)}
                className={`px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${
                  level === l
                    ? (LEVEL_STYLE[l]?.bg + ' ' + LEVEL_STYLE[l]?.text || 'bg-green-900 text-green-300')
                    : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`level-filter-${l}`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Category filter */}
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 outline-none"
            data-testid="category-filter"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-1 flex-1 min-w-48">
            <div className="flex items-center gap-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 flex-1">
              <Search size={13} className="text-gray-500 shrink-0" />
              <input
                type="text"
                placeholder="Search messages, paths, words..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="bg-transparent text-xs text-gray-200 placeholder-gray-600 outline-none flex-1"
                data-testid="log-search-input"
              />
              {searchInput && (
                <button type="button" onClick={() => { setSearchInput(''); setSearch(''); }}
                  className="text-gray-600 hover:text-gray-400">
                  <X size={12} />
                </button>
              )}
            </div>
            <button type="submit"
              className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300 hover:bg-gray-600 transition-colors">
              Go
            </button>
          </form>
        </div>

        {/* ── Stats strip for the selected day ─────────────────────────────── */}
        {dayTotal && (
          <div className="flex items-center gap-4 text-[11px] px-1">
            <span className="text-gray-500">
              <span className="text-gray-300 font-bold">{dayTotal.total}</span> events on {fmtDate(date)}
            </span>
            {dayTotal.ERROR + dayTotal.CRITICAL > 0 && (
              <span className="text-red-400">
                <span className="font-bold">{dayTotal.ERROR + dayTotal.CRITICAL}</span> errors
              </span>
            )}
            {dayTotal.WARNING > 0 && (
              <span className="text-yellow-400">
                <span className="font-bold">{dayTotal.WARNING}</span> warnings
              </span>
            )}
            {stats?.slow_paths?.length > 0 && (
              <span className="text-orange-400">
                Slowest: <span className="font-bold">{stats.slow_paths[0].path}</span>{' '}
                ({stats.slow_paths[0].avg_ms.toFixed(0)}ms avg)
              </span>
            )}
            <span className="text-gray-600">
              Showing {Math.min(entries.length, total)} / {total}
            </span>
          </div>
        )}

        {/* ── Log table ────────────────────────────────────────────────────── */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-hidden">
          {/* Table header */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/60 text-[10px] text-gray-500 border-b border-gray-800 font-bold uppercase tracking-wider">
            <span className="w-20">Time</span>
            <span className="w-16">Level</span>
            <span className="w-24">Category</span>
            <span className="flex-1">Message</span>
            <span className="w-16 text-right">Duration</span>
            <span className="w-4" />
          </div>

          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-600 text-sm gap-2">
              <RefreshCw size={16} className="animate-spin" /> Loading logs…
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-600 gap-2">
              <CheckCircle size={24} />
              <span className="text-sm">No log entries match the current filter.</span>
            </div>
          ) : (
            <>
              {entries.map((e, i) => <LogEntry key={e.log_id || i} entry={e} />)}
              {entries.length < total && (
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="w-full py-2 text-xs text-gray-500 hover:text-gray-300 hover:bg-gray-800/40 transition-colors"
                  data-testid="load-more-logs"
                >
                  {loading ? 'Loading…' : `Load more (${total - entries.length} remaining)`}
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Slow paths breakdown ─────────────────────────────────────────── */}
        {stats?.slow_paths?.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-3">
            <h3 className="text-[11px] text-gray-500 uppercase tracking-wider mb-2 font-bold">Slowest Endpoints — {fmtDate(date)}</h3>
            <div className="space-y-1">
              {stats.slow_paths.map((p, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="text-gray-500 w-4 text-right">{i + 1}.</span>
                  <span className="flex-1 text-gray-300 font-mono truncate">{p.path}</span>
                  <span className={`${p.avg_ms > 3000 ? 'text-red-400' : p.avg_ms > 1000 ? 'text-yellow-400' : 'text-gray-400'}`}>
                    {p.avg_ms.toFixed(0)}ms avg
                  </span>
                  <span className="text-gray-600">{p.max_ms.toFixed(0)}ms max</span>
                  <span className="text-gray-600">{p.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
